// src/components/Console/Staff/pages/ReceptionCheckInOut.jsx

import React, { useEffect, useState, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './ReceptionCheckInOut.css';
import { StateContext } from '../../../../../../StateProvider';
import {
  fetchBookings,
  updateBookingNote,
  checkInBooking,
  checkOutBooking,
  resendReceiptForBooking
} from '../../../../../../Utilities/api';
import { BackButton } from '../../../../../../Utilities/components/Buttons';

/* =========================
   Helpers (NO ES6 CHAINING)
========================= */

function safe(v) {
  return v == null ? '' : String(v);
}

function lower(v) {
  return safe(v).trim().toLowerCase();
}

function normalizeEnum(v) {
  return lower(v).replace('-', '_');
}

function utcDateOnly(d) {
  try {
    return d.toISOString().slice(0, 10);
  } catch (_) {
    return '';
  }
}

function extractPetNames(selectedPets) {
  var names = [];
  if (!Array.isArray(selectedPets)) return names;

  for (var i = 0; i < selectedPets.length; i++) {
    var p = selectedPets[i];
    if (!p) continue;

    if (typeof p === 'string') names.push(p);
    else if (p.petName) names.push(p.petName);
    else if (p.name) names.push(p.name);
    else if (p.pet && p.pet.name) names.push(p.pet.name);
  }
  return names;
}

const ITEMS_PER_PAGE = 10;

/* =========================
   Component
========================= */

export default function ReceptionCheckInOut() {
  const { user } = useContext(StateContext) || {};
  const navigate = useNavigate();
  const searchInputRef = useRef(null);

  /* ---------- state ---------- */
  const [rawBookings, setRawBookings] = useState([]);
  const [normalized, setNormalized] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [dayFilter, setDayFilter] = useState('today');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  const [currentPage, setCurrentPage] = useState(1);

  const [noteDraft, setNoteDraft] = useState('');
  const [activeBookingId, setActiveBookingId] = useState(null);

  var todayUTC = utcDateOnly(new Date());

  /* ---------- fetch ---------- */
  useEffect(function () {
    load(true);
    if (searchInputRef.current) {
      try { searchInputRef.current.focus(); } catch (_) {}
    }
  }, []);

  async function load(showSpinner) {
    if (showSpinner) setLoading(true);
    setError('');

    try {
      var res = await fetchBookings();
      var list = Array.isArray(res) ? res : [];
      setRawBookings(list);
      normalizeBookings(list);
    } catch (e) {
      setError(e && e.message ? e.message : 'Failed to load bookings');
      setRawBookings([]);
      setNormalized([]);
    } finally {
      if (showSpinner) setLoading(false);
    }
  }

  /* ---------- normalize ---------- */
  function normalizeBookings(list) {
    var out = [];

    for (var i = 0; i < list.length; i++) {
      var b = list[i];
      var dateUTC = '';

      if (b && b.startTime) {
        try { dateUTC = utcDateOnly(new Date(b.startTime)); } catch (_) {}
      }

      out.push({
        original: b,
        id: b.id,
        dateUTC: dateUTC,
        status: normalizeEnum(b.status || 'booked'),
        service: normalizeEnum(b.bookingType),
        ownerName: safe(b.ownerName || (b.user && b.user.name)),
        petNames: extractPetNames(b.selectedPets),
        reference: safe(b.reference)
      });
    }

    setNormalized(out);
  }

  /* ---------- filter ---------- */
  var filtered = [];
  for (var j = 0; j < normalized.length; j++) {
    var nb = normalized[j];

    if (dayFilter === 'today' && nb.dateUTC !== todayUTC) continue;
    if (dayFilter === 'past' && nb.dateUTC >= todayUTC) continue;
    if (dayFilter === 'upcoming' && nb.dateUTC <= todayUTC) continue;

    if (serviceFilter !== 'all' && nb.service !== serviceFilter) continue;
    if (statusFilter !== 'all' && nb.status !== statusFilter) continue;

    if (search && search.trim() !== '') {
      var q = lower(search);
      var hit = false;

      if (lower(nb.ownerName).indexOf(q) > -1) hit = true;
      if (safe(nb.id).indexOf(q) > -1) hit = true;
      if (lower(nb.reference).indexOf(q) > -1) hit = true;
      if (lower(nb.service).indexOf(q) > -1) hit = true;

      for (var p = 0; p < nb.petNames.length && !hit; p++) {
        if (lower(nb.petNames[p]).indexOf(q) > -1) hit = true;
      }

      if (!hit) continue;
    }

    filtered.push(nb);
  }

  /* ---------- pagination ---------- */
  var totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  if (currentPage > totalPages) currentPage = 1;

  var pageItems = [];
  var startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  var endIndex = startIndex + ITEMS_PER_PAGE;

  for (var k = startIndex; k < endIndex && k < filtered.length; k++) {
    pageItems.push(filtered[k]);
  }

  /* ---------- actions ---------- */

  async function onCheckIn(nb) {
    var prev = rawBookings.slice();
    var updated = [];

    for (var i = 0; i < prev.length; i++) {
      var b = prev[i];
      updated.push(b.id === nb.id ? { ...b, status: 'checked_in' } : b);
    }

    setRawBookings(updated);
    normalizeBookings(updated);

    try {
      await checkInBooking(nb.id, { at: new Date().toISOString() });
    } catch (_) {
      setRawBookings(prev);
      normalizeBookings(prev);
      alert('Check-in failed');
    }
  }

  async function onCheckOut(nb) {
    var prev = rawBookings.slice();
    var updated = [];

    for (var i = 0; i < prev.length; i++) {
      var b = prev[i];
      updated.push(b.id === nb.id ? { ...b, status: 'checked_out' } : b);
    }

    setRawBookings(updated);
    normalizeBookings(updated);

    try {
      await checkOutBooking(nb.id, { at: new Date().toISOString() });
    } catch (_) {
      setRawBookings(prev);
      normalizeBookings(prev);
      alert('Check-out failed');
    }
  }

  async function onSaveNote() {
    if (!activeBookingId) return;

    try {
      await updateBookingNote(activeBookingId, noteDraft);

      var updated = [];
      for (var i = 0; i < rawBookings.length; i++) {
        var b = rawBookings[i];
        updated.push(b.id === activeBookingId ? { ...b, specialNotes: noteDraft } : b);
      }

      setRawBookings(updated);
      normalizeBookings(updated);
      setActiveBookingId(null);
      setNoteDraft('');
    } catch (_) {
      alert('Saving note failed');
    }
  }

  function openBooking(nb) {
    navigate('/console/booking/' + nb.id, { state: { booking: nb.original } });
  }

  /* ---------- render ---------- */

  if (loading) return <div className="rco-loading">Loading check-ins…</div>;
  if (error) return <div className="rco-error">Error: {error}</div>;

  return (
    <div className="rco-wrap">
      {/* Topbar (Pets-style) */}
      <div className="rco-topbar">
        <BackButton />
        <div className="rco-topbar-right">
          <div className="rco-search">
            <input
              ref={searchInputRef}
              className="rco-input"
              placeholder="Search pet/owner"
              value={search}
              onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
            />
          </div>
        </div>
      </div>

      {/* <h2 className="rco-title">🐾 Reception · Check In / Out</h2> */}

      {/* Filters */}
      <div className="rco-card rco-filters">
        <label className="rco-filter">
          <span>Day</span>
          <select className="rco-select" value={dayFilter} onChange={e => { setDayFilter(e.target.value); setCurrentPage(1); }}>
            <option value="today">Today</option>
            <option value="upcoming">Upcoming</option>
            <option value="past">Past</option>
          </select>
        </label>

        <label className="rco-filter">
          <span>Service</span>
          <select className="rco-select" value={serviceFilter} onChange={e => { setServiceFilter(e.target.value); setCurrentPage(1); }}>
            <option value="all">All</option>
            <option value="daycare">Daycare</option>
            <option value="grooming">Grooming</option>
            <option value="hotel">Hotel</option>
            <option value="training">Training</option>
          </select>
        </label>

        <label className="rco-filter">
          <span>Status</span>
          <select className="rco-select" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}>
            <option value="all">All</option>
            <option value="booked">Booked</option>
            <option value="checked_in">Checked In</option>
            <option value="checked_out">Checked Out</option>
            <option value="canceled">Canceled</option>
          </select>
        </label>
      </div>

      {/* List */}
      {pageItems.length === 0 ? (
        <div className="rco-empty">No bookings match your filters.</div>
      ) : (
        <div className="rco-list">
          {pageItems.map(function (nb) {
            var b = nb.original;
            var when = b.startTime ? new Date(b.startTime).toLocaleString() : '—';
            var canCheckIn = nb.status === 'booked';
            var canCheckOut = nb.status === 'checked_in';

            return (
              <div key={nb.id} className="rco-row">
                <button className="rco-main" onClick={() => openBooking(nb)}>
                  <div className="rco-line1">
                    <span className="rco-id">#{nb.id}</span>
                    <span className={'rco-badge ' + nb.status}>{nb.status}</span>
                    <span className="rco-type">{nb.service}</span>
                    <span className="rco-when">{when}</span>
                  </div>
                  <div className="rco-line2">
                    <span>Pets: {nb.petNames.join(', ') || '—'}</span>
                    <span>Owner: {nb.ownerName}</span>
                    <span>Ref: {nb.reference}</span>
                    {b.specialNotes ? <span className="rco-notes">📝 {b.specialNotes}</span> : null}
                  </div>
                </button>

                <div className="rco-actions">
                  {canCheckIn && <button className="rco-btn rco-btn--primary" onClick={() => onCheckIn(nb)}>Check In</button>}
                  {canCheckOut && <button className="rco-btn rco-btn--warn" onClick={() => onCheckOut(nb)}>Check Out</button>}
                  <button className="rco-btn rco-btn--ghost" onClick={() => resendReceiptForBooking(nb.id)}>Receipt</button>
                  <button className="rco-btn" onClick={() => { setActiveBookingId(nb.id); setNoteDraft(b.specialNotes || ''); }}>Note</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      <div className="rco-pagination">
        <button className="rco-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>Previous</button>
        <span className="rco-pageinfo">Page {currentPage} of {totalPages}</span>
        <button className="rco-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>Next</button>
      </div>

      {/* Note modal */}
      {activeBookingId && (
        <div className="rco-note-modal">
          <div className="rco-note-card">
            <div className="rco-note-title">Update Note · #{activeBookingId}</div>
            <textarea
              className="rco-note-text"
              value={noteDraft}
              onChange={e => setNoteDraft(e.target.value)}
            />
            <div className="rco-note-actions">
              <button className="rco-btn rco-btn--primary" onClick={onSaveNote}>Save</button>
              <button className="rco-btn rco-btn--ghost" onClick={() => setActiveBookingId(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
